export type DebugStage = "capture" | "codes" | "ocr" | "parse" | "lookup";

export interface PriceTagDebugEvent {
  stage: DebugStage;
  status: "started" | "success" | "error";
  elapsedMs: number;
  data?: unknown;
  error?: { name: string; message: string };
}

export interface PriceTagDebugEntry {
  id: string;
  timestamp: string;
  storeId?: string;
  userAgent: string;
  viewport: { width: number; height: number; pixelRatio: number };
  events: PriceTagDebugEvent[];
  syncedRevision?: number;
  uploadError?: string;
}

export type DebugUpload = (entry: PriceTagDebugEntry) => Promise<{ recorded: boolean; attemptId: string; revision: number }>;

/** Only diagnostic content crosses the wire; sync state and previews stay local. */
export function debugUploadPayload(entry: PriceTagDebugEntry): PriceTagDebugEntry {
  return { id: entry.id, timestamp: entry.timestamp, storeId: entry.storeId, userAgent: entry.userAgent, viewport: entry.viewport, events: entry.events };
}

export interface PriceTagDebugObserver {
  event: (event: PriceTagDebugEvent) => void;
  frame: (dataUrl: string) => void;
}

export const DEBUG_STORAGE_KEY = "fit.priceTag.debug.v1";
export const DEBUG_ENABLED_KEY = "fit.priceTag.debug.enabled";
export const DEBUG_MAX_ENTRIES = 20;
const MAX_STORED_CHARS = 1_000_000;

export function debugError(error: unknown): { name: string; message: string } {
  if (error instanceof Error) return { name: error.name, message: error.message };
  if (typeof error === "object" && error !== null) {
    const value = error as { code?: unknown; message?: unknown };
    return { name: String(value.code ?? "Error"), message: String(value.message ?? "Unknown error") };
  }
  return { name: "Error", message: String(error) };
}

export function loadDebugEntries(): PriceTagDebugEntry[] {
  try {
    const raw = localStorage.getItem(DEBUG_STORAGE_KEY);
    if (!raw || raw.length > MAX_STORED_CHARS) return [];
    const entries: unknown = JSON.parse(raw);
    if (!Array.isArray(entries)) return [];
    return entries.filter((entry): entry is PriceTagDebugEntry =>
      typeof entry?.id === "string" && typeof entry?.timestamp === "string" &&
      Array.isArray(entry?.events) && entry.events.every((event: PriceTagDebugEvent) =>
        event && ["capture", "codes", "ocr", "parse", "lookup"].includes(event.stage) &&
        ["started", "success", "error"].includes(event.status) && typeof event.elapsedMs === "number"),
    ).slice(0, DEBUG_MAX_ENTRIES);
  } catch {
    return [];
  }
}

/** Keep complete attempts: evict older entries instead of truncating OCR text. */
export function saveDebugEntries(entries: PriceTagDebugEntry[]): boolean {
  const saved = entries.slice(0, DEBUG_MAX_ENTRIES);
  while (saved.length > 1 && JSON.stringify(saved).length > MAX_STORED_CHARS) saved.pop();
  try {
    const json = JSON.stringify(saved);
    if (json.length > MAX_STORED_CHARS) return false;
    localStorage.setItem(DEBUG_STORAGE_KEY, json);
    return true;
  } catch {
    return false;
  }
}

export function exportDebugEntries(entries: PriceTagDebugEntry[]): string {
  return JSON.stringify({ schemaVersion: 1, exportedAt: new Date().toISOString(), entries }, null, 2);
}
